import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useServiceOnboarding } from 'contexts/service-onboarding'
import { useCreateGroup } from 'state/group'
import { size } from 'utilities/object'
import { compact } from 'utilities/array'
import { cls } from 'utilities/dom'
import Modal, { useModal } from 'modals/dialog'
import Form from 'components/form/controller'
import ContractableSelectField from 'components/form/field/contractable-select'
import StringField from 'components/form/field/string'
import TextField from 'components/form/field/text'
import AddressField from 'components/form/field/address'
import PeopleField from 'components/form/field/people'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'
import InvalidWarning from './invalid-warning'

const AddGroup = ({
    group: externalGroup = null, type: initialType = null,
    onDone, doneLabel, salt
}) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()
    const { getAvailableGroupTypes } = useAccess()

    const {
        onboarder,
        updateOnboardingStatus
    } = useServiceOnboarding()

    const availableTypes = getAvailableGroupTypes()

    const {
        mutateAsync: createGroup,
        isPending: creating
    } = useCreateGroup()

    const typeOptions = compact([
        availableTypes.includes('company') && {
            value: 'company',
            label: formatMessage({
                id: 'noun_company',
                defaultMessage: 'Company'
            })
        },
        availableTypes.includes('team') && {
            value: 'team',
            label: formatMessage({
                id: 'noun_team',
                defaultMessage: 'Team'
            })
        },
        availableTypes.includes('location') && {
            value: 'location',
            label: formatMessage({
                id: 'noun_location',
                defaultMessage: 'Location'
            })
        }
    ])

    const [type, setType] = useState(availableTypes.includes(initialType) ?
        initialType :
        (typeOptions.length === 1) ?
            typeOptions[0].value :
            null
    )

    const [address, setAddress] = useState(null)
    const [created, setCreated] = useState(null)

    const add = async body => {
        if(!body.organizationNumber?.trim?.()) {
            delete body.organizationNumber
        }

        const { ok, response } = await createGroup({
            type,
            body
        })

        if(ok && response) {
            !!onboarder && updateOnboardingStatus({
                [getServiceOnboardingName(type)]: 'completed'
            })

            if(!!response.invalidOrganizationNumber) {
                return void setCreated(response)
            }

            onDone(response)
        }
    }

    const headingTranslation = getHeadingTranslation(type) ?? {
        id: 'group_action_add',
        defaultMessage: 'Add group'
    }

    if(!!created) {
        return (
            <InvalidWarning
                group={created}
                onDone={(keepEditing = true) => onDone(created, keepEditing)} />
        )
    }

    return (
        <>
            <Modal.Header
                heading={formatMessage(headingTranslation)}
                showDismiss={false} />
            <Form
                layout="vertical"
                onSubmit={add}>
                {({ touched, errors, trigger }) => (
                    <>
                        <Modal.Body>
                            {(!initialType && typeOptions.length > 1) && (
                                <ContractableSelectField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_type',
                                        defaultMessage: 'Type'
                                    })}
                                    name="type"
                                    field={{
                                        value: type ?? '',
                                        required: true,
                                        include: 'always',
                                        unsettable: false,
                                        unsetOnPick: false
                                    }}
                                    options={typeOptions}
                                    onChange={({ type }) => setType(type)} />
                            )}
                            {!!type && (
                                <>
                                    <StringField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'noun_name',
                                            defaultMessage: 'Name'
                                        })}
                                        name="name"
                                        field={{
                                            value: externalGroup?.name,
                                            required: true,
                                            include: 'always'
                                        }}
                                        {...(!!initialType ? {
                                            controlProps: {
                                                autoFocus: true
                                            }
                                        } : null)} />
                                    {(type === 'company') && (
                                        <StringField
                                            salt={salt}
                                            label={formatMessage({
                                                id: 'payment_label_organization_number',
                                                defaultMessage: 'Organization number'
                                            })}
                                            name="organizationNumber"
                                            field={{
                                                include: 'always',
                                                softRequired: true
                                            }} />
                                    )}
                                    {['company', 'location'].includes(type) && (
                                        <AddressField
                                            salt={salt}
                                            label={formatMessage({
                                                id: 'noun_address',
                                                defaultMessage: 'Address'
                                            })}
                                            name="address"
                                            field={{
                                                value: address,
                                                include: 'always',
                                                softRequired: type === 'company'
                                            }}
                                            onChange={({ address }) => setAddress(address)} />
                                    )}
                                    <TextField
                                        salt={salt}
                                        label={formatMessage({
                                            id: 'noun_description',
                                            defaultMessage: 'Description'
                                        })}
                                        name="description"
                                        field={{ include: 'always' }} />
                                    <PeopleField
                                        name="members"
                                        label={formatMessage({
                                            id: 'noun_members',
                                            defaultMessage: 'Members'
                                        })} />
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Actions>
                                <Plain
                                    onClick={dismiss}
                                    className="neutral"
                                    disabled={creating}>
                                    <FormattedMessage
                                        id="action_cancel"
                                        defaultMessage="Cancel" />
                                </Plain>
                                <ButtonSubmit
                                    className={cls([
                                        'constructive',
                                        creating && 'loading'
                                    ])}
                                    disabled={!touched.length || !!size(errors) || creating}
                                    ref={trigger}>
                                    {!!doneLabel && doneLabel}
                                    {!doneLabel && (
                                        <FormattedMessage
                                            id="action_continue"
                                            defaultMessage="Continue" />
                                    )}
                                </ButtonSubmit>
                            </Actions>
                        </Modal.Footer>
                    </>
                )}
            </Form>
        </>
    )
}

const getServiceOnboardingName = type => ({
    company: 'addCompany',
    team: 'addTeam',
    location: 'addLocation'
})[type]

const getHeadingTranslation = type => ({
    company: {
        id: 'company_action_add',
        defaultMessage: 'Add company'
    },
    team: {
        id: 'team_action_add',
        defaultMessage: 'Add team'
    },
    location: {
        id: 'location_action_add',
        defaultMessage: 'Add location'
    }
})[type]

export default AddGroup